import { Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { SEO } from "@/components/SEO";
import { MarketingFooter } from "@/components/MarketingFooter";

const RefundPolicy = () => {
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SEO 
        title="Refund Policy" 
        description="Understand how refunds work for split payments on Splitkart, including gift vouchers and merchant responsibilities."
        url="/refund-policy"
      />
      <Header />
      
      <main className="flex-1 container mx-auto px-4 py-8 max-w-4xl">
        <h1 className="text-4xl font-bold mb-8">Refund Policy</h1>
        <p className="text-muted-foreground mb-8">Last updated: January 2025</p>

        <div className="prose prose-gray dark:prose-invert max-w-none space-y-6">
          <section>
            <h2 className="text-2xl font-semibold mb-4">1. Overview</h2>
            <p className="text-muted-foreground">
              Splitkart is a split payment platform that facilitates group purchases on Shopify stores. This policy explains how refunds are handled when a split payment does not complete or when participants request their money back.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">2. Successful Split Payments</h2>
            <p className="text-muted-foreground">
              When all participants complete their payments before the deadline, the purchase is finalized and the product is fulfilled by the merchant. Refunds for fulfilled orders are handled directly by the merchant according to their store's refund policy.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">3. Incomplete Split Payments</h2>
            <p className="text-muted-foreground mb-3">
              If a split payment does not reach 100% completion by the deadline:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li><strong>Gift Vouchers:</strong> Participants who have paid will automatically receive gift vouchers for the full amount of their contribution (including any applicable taxes).</li>
              <li><strong>Voucher Validity:</strong> Gift vouchers can be redeemed at the merchant's store for future purchases.</li>
              <li><strong>Inventory Release:</strong> Any reserved inventory is automatically released back to the merchant's stock.</li>
              <li><strong>Cash Refunds:</strong> If you prefer a cash refund instead of a voucher, contact the merchant directly. The merchant will process the refund through their payment gateway.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">4. Refund Process</h2>
            <p className="text-muted-foreground mb-3">
              Splitkart operates as a payment facilitator, not a payment processor. Here's how refunds work:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li><strong>Voucher Issuance:</strong> Automatic upon split payment expiration</li>
              <li><strong>Cash Refund Requests:</strong> Submit to the merchant via their contact channels</li>
              <li><strong>Processing Time:</strong> Merchants typically process refunds within 5-10 business days</li>
              <li><strong>Refund Method:</strong> Refunds are issued to the original payment method used</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">5. Cancellation Fees</h2>
            <p className="text-muted-foreground">
              Some merchants may configure cancellation fees for incomplete split payments. If applicable, these fees will be deducted from the gift voucher amount. The initiator of the split payment is responsible for any cancellation fees as disclosed at the time of purchase.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">6. Merchant Subscription Refunds</h2>
            <p className="text-muted-foreground mb-3">
              For merchants using Splitkart's subscription plans:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li><strong>Trial Period:</strong> No charges during the 30-day trial. Cancel anytime without penalty.</li>
              <li><strong>Shopify Monthly Plans:</strong> Cancel before your next billing date to avoid future recurring charges. No refunds are provided for partial months already approved through Shopify billing.</li>
              <li><strong>Free Plan Overage Fees:</strong> Usage-based transaction fees charged after the included free splits are non-refundable once processed.</li>
              <li><strong>Starter and Growth Overage Fees:</strong> Usage-based transaction fees charged after the included free split allowance are non-refundable once processed.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">7. Disputes</h2>
            <p className="text-muted-foreground">
              If you believe you are entitled to a refund that has not been processed, please first contact the merchant directly. If the issue remains unresolved, contact Splitkart support at refunds@splitkart.app with your order details and we will assist in mediating the dispute.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">8. Contact Us</h2>
            <p className="text-muted-foreground">
              For questions about this Refund Policy or to request assistance with a refund, please contact us at{" "}
              <a href="mailto:refunds@splitkart.app" className="text-primary hover:underline">
                refunds@splitkart.app
              </a>{" "}
              or visit our{" "}
              <Link to="/contact" className="text-primary hover:underline">
                Contact page
              </Link>.
            </p>
          </section>
        </div>
      </main>
      
      <MarketingFooter />
    </div>
  );
};

export default RefundPolicy;
